import { useApiCall } from '@/hooks/useApiCall';
import { FunctionComponent, useEffect, useState } from 'react';
import { locationService } from '../../api/locations/location.service';
import { LocationAssetModel } from '../../types/LocationAssetModel';
import { GuestAssetsAtLocationGrid } from './GuestAssetsAtLocationGrid';
import { useParams, useSearchParams } from 'react-router-dom';
import { DisplayMode } from '@/types/display-mode.enum';
import LocationHierarchyToggleButton from '../LocationHierarchyToggleButton';
import { PagePaginationResultDto, PageSortOption, PageSortOrder } from '@/lib/api/pagination.page.dto';
import PagedResultDataText from '@/components/filterbar/PagedResultDataText';
import PaginationControls from '@/components/grid/PaginationControls';
import useQueryParamsFilters from '@/hooks/useQueryParamFilters';
import { CacheKey } from '@/providers/cache-provider/cache-key.enum';
import { FilterFieldName, FilterValues } from '@/components/filterbar/FilterBarContext';
import { useTranslation } from '@/lib';
import FilterBar from '@/components/filterbar/FilterBar';
import AssetTypesFilter from '@/components/filterbar/filters/AssetTypesFilter';
import DateFilter from '@/components/filterbar/filters/DateFilter';
import NumberFilter from '@/components/filterbar/filters/NumberFilter';
import { GridApi } from 'ag-grid-community';
import { useExportToExcel } from '@/hooks/useExportToExcel';
import dayjs from 'dayjs';

const ASSET_FETCH_LIMIT = 1000;

export const GuestLocationAssetsTab: FunctionComponent = () => {
  const { locationId } = useParams();
  if (!locationId) {
    return;
  }

  const { t } = useTranslation();

  const locationIdNumber = Number.parseInt(locationId);

  const [searchParams] = useSearchParams();
  const { setFiltersToUrl, filters } = useQueryParamsFilters(
    {
      pageNumber: searchParams.get('pageNumber') ? Number.parseInt(searchParams.get('pageNumber')!) : 1,
    },
    CacheKey.LOCATION_ASSET_FILTERS,
  );

  const { exportToExcel } = useExportToExcel();

  const [displayMode, setDisplayMode] = useState<DisplayMode>(DisplayMode.INDIVIDUAL);

  const {
    data: assetData,
    isLoading: assetIsLoading,
    isError: assetIsError,
    setApiCallArg,
  } = useApiCall<PagePaginationResultDto<LocationAssetModel>>(() => getAssetsIndividual());

  const getAssetsHierarchical = (page?: number) => {
    return locationService.getAssetsHierarchical(
      {
        locationId: filters.current.locationId ?? locationIdNumber,
      },
      {
        page: page ?? filters.current.pageNumber,
        limit: ASSET_FETCH_LIMIT,
        order: filters.current.sortDirection,
        sort: filters.current.sortOption,
      },
      {
        assetTypeIds: filters.current.assetTypeIds as number[],
        arrivedAtDateFrom: filters.current.arrivedAtDateFrom?.toISOString(),
        arrivedAtDateTo: filters.current.arrivedAtDateTo?.toISOString(),
        stayTimeInDays: filters.current.stayTimeInDays,
      },
    );
  };

  const getAssetsIndividual = (page?: number) => {
    return locationService.getAssets(
      {
        locationId: filters.current.locationId ?? locationIdNumber,
      },
      {
        page: page ?? filters.current.pageNumber,
        limit: ASSET_FETCH_LIMIT,
        order: filters.current.sortDirection,
        sort: filters.current.sortOption,
      },
      {
        assetTypeIds: filters.current.assetTypeIds as number[],
        arrivedAtDateFrom: filters.current.arrivedAtDateFrom?.toISOString(),
        arrivedAtDateTo: filters.current.arrivedAtDateTo?.toISOString(),
        stayTimeInDays: filters.current.stayTimeInDays,
      },
    );
  };

  function getAssets(displayMode: DisplayMode, page?: number) {
    if (displayMode === DisplayMode.HIERARCHICAL) {
      return getAssetsHierarchical(page);
    } else {
      return getAssetsIndividual(page);
    }
  }

  useEffect(() => {
    setApiCallArg(() => getAssets(displayMode));
  }, [displayMode]);

  const handleSearch = (filterValues: FilterValues, page?: number) => {
    setFiltersToUrl({
      ...filterValues,
      pageNumber: page ?? filters.current.pageNumber,
    });

    setApiCallArg(() => getAssets(displayMode));
  };

  const onPageChanged = (event: React.ChangeEvent<unknown>, page: number) => {
    handleSearch(
      {
        ...filters.current,
        pageNumber: page,
      },
      page,
    );
  };

  const onSortChange = (sortOption?: PageSortOption, sortDirection?: PageSortOrder) => {
    handleSearch({
      ...filters.current,
      sortOption,
      sortDirection,
    });
  };

  // Export functionality for guest users
  async function onExportToExcelClicked(_gridApi: GridApi<LocationAssetModel> | undefined) {
    if (_gridApi) {
      const data: LocationAssetModel[] = [];
      const dataPage1 = await getAssets(displayMode, 1);

      if (dataPage1.isSuccess) {
        data.push(...dataPage1.payload.data);
      }

      // Fetch additional pages if needed
      for (let i = 2; i <= (dataPage1.payload.totalPages || 1); i++) {
        const dataPage = await getAssets(displayMode, i);

        if (dataPage.isSuccess) {
          data.push(...dataPage.payload.data);
        }
      }

      if (data.length > 0) {
        exportToExcel(
          data.map((locationAsset) => {
            const arrivedAt = dayjs(locationAsset.dto.dateArrivedAtLocation);
            const stayTime = dayjs().diff(arrivedAt, 'days');
            return {
              AssetType: locationAsset.dto.assetTypeName,
              AssetCode: locationAsset.dto.assetCode,
              DateArrived: arrivedAt.format('YYYY-MM-DD'),
              TimeArrived: arrivedAt.format('HH:mm:ss'),
              StayTimeDays: stayTime,
            };
          }),
          `assets_${dayjs().format('YYYY_MM_DD_HHmmss')}.xlsx`,
        );
      }
    }
  }

  const filterComponents = [
    { label: t('asset_types'), filterName: FilterFieldName.assetTypeIds, filter: <AssetTypesFilter /> },
    {
      label: t('arrived_at_date_from'),
      filterName: FilterFieldName.arrivedAtDateFrom,
      filter: <DateFilter label={t('arrived_at_date_from')} filterField={FilterFieldName.arrivedAtDateFrom} />,
    },
    {
      label: t('arrived_at_date_to'),
      filterName: FilterFieldName.arrivedAtDateTo,
      filter: <DateFilter label={t('arrived_at_date_to')} filterField={FilterFieldName.arrivedAtDateTo} />,
    },
    {
      label: t('minimum_stay_time_days'),
      filterName: FilterFieldName.stayTimeInDays,
      filter: <NumberFilter label={t('minimum_stay_time_days')} filterFieldName={FilterFieldName.stayTimeInDays} />,
    },
  ];

  return (
    <div className="flex h-full flex-1 flex-grow  max-w-[1920px] ">
      <div className="flex flex-col h-full w-full">
        <div className="">
          <div className="flex items-center justify-end mb-2">
            <LocationHierarchyToggleButton displayMode={displayMode} reverseOrder onChange={setDisplayMode} />
          </div>
          <div className="mb-2">
            <FilterBar
              showAsPopover
              onSearch={handleSearch}
              initialFilterValues={filters.current}
              filterComponents={filterComponents}
              isLoading={assetIsLoading}
            ></FilterBar>
          </div>
        </div>
        <GuestAssetsAtLocationGrid
          data={assetData?.data}
          isError={assetIsError}
          isLoading={assetIsLoading}
          onSortChange={onSortChange}
          sortOption={filters.current.sortOption}
          sortDirection={filters.current.sortDirection}
          onExportToExcelClicked={onExportToExcelClicked}
        ></GuestAssetsAtLocationGrid>

        <div className="flex items-center gap-x-4">
          <PaginationControls
            isLoading={assetIsLoading}
            totalPageCount={assetData?.totalPages ?? 1}
            currentPage={filters.current.pageNumber ?? 1}
            totalElements={assetData?.totalElements ?? 0}
            onChange={onPageChanged}
          />
          <PagedResultDataText data={assetData} name={t('asset', { count: assetData?.totalElements ?? 0 })} />
        </div>
      </div>
    </div>
  );
};
